import React, { useMemo, useState, useCallback } from 'react';
import { shape, string, arrayOf, array, object, bool, func, number } from 'prop-types';
import { propTypes } from '../../../../../util/types';
import { FormattedMessage } from '../../../../../util/reactIntl';
import { getMarketplaceEntities } from '../../../../../ducks/marketplaceData.duck';
import { types as sdkTypes } from '../../../../../util/sdkLoader';
import { ensureUser } from '../../../../../util/data';
import { Avatar, ActionButton, IconComment, IconBin, Badges, NamedLink, IconVote, InlineTextButton } from '../../../../../components';
import ControlMenu from './ControlMenu/ControlMenu';
import { useCommentVotes } from './Comment.shared';
import { CommunityCommentForm } from '../../../../../forms';
import { convertToRaw } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import { sanitizeRichTextHtml, enhanceRichTextHtml } from '../../../../../util/richText';
import classNames from 'classnames';
import moment from 'moment';

import css from './Comment.module.css';

const { UUID } = sdkTypes;
const isBetaMode = process.env.REACT_APP_BETA_MODE === 'true';

// Maximum nesting depth to prevent excessive indentation
const MAX_VISIBLE_DEPTH = 5;

/**
 * Converts mention links in HTML to React Router NamedLink components
 * This prevents full page refreshes when clicking mentions
 */
const convertMentionLinksToNamedLinks = (htmlContent) => {
  if (!htmlContent) return null;

  // Sanitize before any rendering path. SSR uses regex-based stripping;
  // the client uses DOMPurify. See util/richText.js.
  const safeHtml = sanitizeRichTextHtml(htmlContent);

  // Server-side fallback: render the enhanced HTML (sanitize + link rewrite).
  if (typeof window === 'undefined') {
    return <div dangerouslySetInnerHTML={{ __html: enhanceRichTextHtml(htmlContent) }} />;
  }

  // Parse the (already sanitized) HTML and walk the DOM to swap mention
  // links for React Router NamedLinks (avoids a full reload when clicked).
  const parser = new DOMParser();
  const doc = parser.parseFromString(safeHtml, 'text/html');
  
  // Function to recursively convert DOM nodes to React elements
  const domToReact = (node, key = 0) => {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent;
    }
    
    if (node.nodeType === Node.ELEMENT_NODE) {
      const tagName = node.tagName.toLowerCase();
      
      // Convert mention links to NamedLink → ProfilePage (works in both
      // marketplace and community modes now that beta has profiles).
      if (tagName === 'a' && node.getAttribute('href')?.startsWith('/user/')) {
        const userId = node.getAttribute('href').split('/user/')[1];
        const linkText = node.textContent;

        return (
          <NamedLink
            key={key}
            name="ProfilePage"
            params={{ id: userId }}
            className="wysiwyg-mention"
          >
            {linkText}
          </NamedLink>
        );
      }
      
      // Convert other elements to React elements
      const props = { key };

      // Non-mention links open in new tab
      if (tagName === 'a' && node.getAttribute('rel') !== 'mention') {
        props.target = '_blank';
        props.rel = 'noopener noreferrer';
      }

      // Lazy-load images embedded in the comment body
      if (tagName === 'img') {
        props.loading = 'lazy';
        props.decoding = 'async';
      }

      // Copy attributes
      for (let i = 0; i < node.attributes.length; i++) {
        const attr = node.attributes[i];
        if (attr.name === 'class') {
          props.className = attr.value;
        } else if (attr.name === 'style') {
          props.style = attr.value.split(';').reduce((acc, style) => {
            const [property, value] = style.split(':').map(s => s.trim());
            if (property && value) {
              const camelCaseProp = property.replace(/-([a-z])/g, (match, letter) => letter.toUpperCase());
              acc[camelCaseProp] = value;
            }
            return acc;
          }, {});
        } else {
          props[attr.name] = attr.value;
        }
      }
      
      // Convert children
      const children = Array.from(node.childNodes).map((child, index) => 
        domToReact(child, `${key}-${index}`)
      );
      
      return React.createElement(tagName, props, ...children);
    }
    
    return null;
  };
  
  // Convert the body's children to React elements
  const children = Array.from(doc.body.childNodes).map((child, index) => 
    domToReact(child, index)
  );
  
  return <div>{children}</div>;
};

/**
 * Component that displays a single comment with user avatar, name, timestamp, and actions.
 * Recursively renders any replies to this comment.
 */
const Comment = props => {
  const {
    // Context props
    state,
    currentUser,
    intl,

    // Comment data
    comment,
    allComments,

    // Action handlers
    onAddComment,
    onDeleteComment,
    onEditComment,
    onVoteComment,
    postId,

    // UI state props
    addCommentError,
    deleteCommentInProgress,
    deleteCommentError,
    editCommentInProgress,
    editCommentError,
    deletedCommentIds,
    currentDeleteCommentId,

    // Control menu props
    canPinComment = false,
    onPinComment,

    // Nesting props
    isReply = false,
    depth = 0,

    // Confirmation modal props
    onOpenConfirmationModalWithHandlers,
    onUpdateConfirmationModalProps,
    onCloseConfirmationModal,
  } = props;

  // Local state
  const [isReplying, setIsReplying] = useState(false);
  const [isSubmittingReply, setIsSubmittingReply] = useState(false);
  const [isEditComment, setIsEditComment] = useState(false);
  
  // Voting functionality
  const { voteCount, userVote, handleVote, isVoting } = useCommentVotes(
    comment,
    onVoteComment,
    currentUser,
    postId
  );

  // Early return if comment is null
  if (!comment) {
    return null;
  }

  // Extract comment data
  const {
    id: commentId,
    content,
    userId,
    postedAt: postedAtString,
    isDeleted: isSoftDeleted,
    deletedAt,
  } = comment;

  // Convert mention links to React Router Links (sanitization removed for SSR compatibility)
  const commentHtml = useMemo(() => {
    if (!content) return null;

    const convertedContent = convertMentionLinksToNamedLinks(content);

    return (
      <div className={css.commentHtml}>
        {convertedContent}
      </div>
    );
  }, [content]);

  // Process user data
  const commentUserId = useMemo(() => new UUID(userId), [userId]);
  const currentUserId = currentUser?.id?.uuid;
  const isOwnComment = commentUserId.uuid === currentUserId;

  // Get the user data - either current user or from marketplace entities
  const user = useMemo(() => {
    if (isOwnComment) {
      return currentUser;
    }

    const users = getMarketplaceEntities(state, [{ type: 'user', id: commentUserId }]);
    return ensureUser(users?.[0]);
  }, [isOwnComment, currentUser, state, commentUserId]);

  // Format timestamp
  const postedAt = useMemo(() => {
    try {
      return moment(JSON.parse(postedAtString)).fromNow();
    } catch (e) {
      console.error('Error parsing comment date:', e);
      return ''; // Fallback for invalid date
    }
  }, [postedAtString]);

  // Format deletion timestamp if available
  const deletionTime = useMemo(() => {
    if (!deletedAt) {
      return null;
    }
    try {
      return moment(JSON.parse(deletedAt)).fromNow();
    } catch (e) {
      return null;
    }
  }, [deletedAt]);

  // Find replies to this comment
  const replies = useMemo(() => {
    if (!allComments) {
      return [];
    }
    return allComments.filter(c => c.parentId === commentId);
  }, [allComments, commentId]);

  // Check if this comment has replies (used to determine delete behavior)
  const hasReplies = replies.length > 0;

  // UI state
  const isCurrentlyDeleting = currentDeleteCommentId === commentId;
  const isHardDeleted = deletedCommentIds.includes(commentId);
  const isCommentDeleted = isHardDeleted || isSoftDeleted;
  const showDeleteError = isCurrentlyDeleting && deleteCommentError;

  // Calculate effective depth for styling
  const effectiveDepth = Math.min(depth, MAX_VISIBLE_DEPTH);

  // Class names with added nesting depth
  const classes = classNames(css.root, {
    [css.inProgress]: deleteCommentInProgress && isCurrentlyDeleting,
    [css.deleted]: isCommentDeleted,
    [css.reply]: isReply,
    [css[`depth${effectiveDepth}`]]: depth > 0,
  });

  // Event handlers - using useCallback to optimize performance
  const handleDelete = useCallback(() => {
    if (!deleteCommentInProgress && onOpenConfirmationModalWithHandlers) {
      // Show confirmation modal before deleting
      onOpenConfirmationModalWithHandlers({
        id: 'PostPage.confirmationModal',
        title: intl.formatMessage({
          id: 'PostPage.deleteCommentConfirmationModalTitle',
        }),
        description: intl.formatMessage({
          id: 'PostPage.deleteCommentConfirmationModalDescription',
        }),
        onConfirm: async () => {
          try {
            // Set loading state on the confirm button
            onUpdateConfirmationModalProps({ inProgress: true });
            
            // Perform the deletion
            await onDeleteComment(commentId, hasReplies);
            
            // Close modal after successful deletion
            setTimeout(() => {
              onCloseConfirmationModal();
            }, 500);
          } catch (error) {
            // Handle error case
            onCloseConfirmationModal();
            console.error('Failed to delete comment:', error);
          }
        },
        onCancel: () => {
          // User cancelled, just close modal
          onCloseConfirmationModal();
        },
      });
    } else if (!deleteCommentInProgress) {
      // Fallback if confirmation modal handlers are not available
      onDeleteComment(commentId, hasReplies);
    }
  }, [
    deleteCommentInProgress,
    onDeleteComment,
    commentId,
    hasReplies,
    onOpenConfirmationModalWithHandlers,
    onUpdateConfirmationModalProps,
    onCloseConfirmationModal,
    intl,
  ]);

  const handleEdit = useCallback(() => {
    setIsEditComment(true);
  }, []);

  const handleEditSubmit = useCallback(
    async values => {
      if (onEditComment) {
        try {
          let htmlContent;
          
          // CommunityCommentForm uses 'content' field name
          const commentData = values.content;
          
          if (typeof commentData === 'string') {
            // New Lexical editor outputs HTML directly
            htmlContent = commentData;
          } else if (commentData && commentData.getCurrentContent) {
            // Legacy Draft.js format
            htmlContent = draftToHtml(convertToRaw(commentData.getCurrentContent()));
          } else {
            htmlContent = '';
          }
          
          await onEditComment(commentId, htmlContent);
          setIsEditComment(false);
        } catch (error) {
          console.error('Error editing comment:', error);
          // Don't close the edit form if there's an error
        }
      }
    },
    [onEditComment, commentId]
  );

  const handleEditCancel = useCallback(() => {
    setIsEditComment(false);
  }, []);

  const handlePinComment = useCallback(() => {
    if (onPinComment) {
      onPinComment(commentId);
    }
  }, [onPinComment, commentId]);

  const handleReplySubmit = useCallback(
    async values => {
      let html;
      
      if (typeof values.content === 'string') {
        // New Lexical editor outputs HTML directly
        html = values.content;
      } else if (values.content.getCurrentContent) {
        // Legacy Draft.js format
        html = draftToHtml(convertToRaw(values.content.getCurrentContent()));
      } else {
        html = '';
      }
      try {
        setIsSubmittingReply(true);

        await onAddComment({
          userId: currentUser.id.uuid,
          parentId: commentId,
          content: html,
        });

        // Only close the form if submission was successful
        setIsReplying(false);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error submitting reply:', error);
      } finally {
        setIsSubmittingReply(false);
      }
    },
    [onAddComment, currentUser?.id?.uuid, commentId]
  );

  const handleStartReplying = useCallback(() => setIsReplying(true), []);
  const handleCancelReplying = useCallback(() => setIsReplying(false), []);

  /**
   * Renders the children replies of this comment
   */
  const renderReplies = () => {
    if (replies.length === 0) {
      return null;
    }

    return (
      <div className={css.repliesContainer}>
        {replies.map(reply => (
          <Comment
            key={reply.id}
            comment={reply}
            allComments={allComments}
            state={state}
            currentUser={currentUser}
            intl={intl}
            onAddComment={onAddComment}
            onDeleteComment={onDeleteComment}
            onEditComment={onEditComment}
            onVoteComment={onVoteComment}
            postId={postId}
            addCommentError={addCommentError}
            deleteCommentInProgress={deleteCommentInProgress}
            deleteCommentError={deleteCommentError}
            editCommentInProgress={editCommentInProgress}
            editCommentError={editCommentError}
            deletedCommentIds={deletedCommentIds}
            currentDeleteCommentId={currentDeleteCommentId}
            onOpenConfirmationModalWithHandlers={onOpenConfirmationModalWithHandlers}
            onUpdateConfirmationModalProps={onUpdateConfirmationModalProps}
            onCloseConfirmationModal={onCloseConfirmationModal}
            isReply={true}
            depth={effectiveDepth + 1}
          />
        ))}
      </div>
    );
  };

  // Render deleted comment view (for both hard-deleted and soft-deleted comments)
  if (isCommentDeleted) {
    return (
      <div className={classes}>
        <Avatar className={css.avatar} initialsClassName={css.avatarInitials} user={user} disableProfileLink={isBetaMode} />
        <div className={css.info}>
          <div className={css.userInfo}>
            <span className={css.deletedMessage}>
              <FormattedMessage id="PostPage.deletedCommentMessage" />
            </span>
            <span className={css.postedAt}>
              {deletionTime || <FormattedMessage id="PostPage.deletedCommentTime" />}
            </span>
          </div>

          {/* Render replies to deleted comments if there are any */}
          {renderReplies()}
        </div>
      </div>
    );
  }

  // Render normal comment
  return (
    <div id={commentId} className={classes}>
      <Avatar
        className={css.avatar}
        initialsClassName={css.avatarInitials}
        user={user}
        useUserTier
              />
      <div className={css.info}>
        {/* Main comment content wrapper - separate from replies */}
        <div className={css.commentContent}>
          {/* User info and timestamp */}
          <div className={css.userInfo}>
            {user?.id?.uuid ? (
              <NamedLink
                name="ProfilePage"
                params={{ id: user.id.uuid }}
                className={css.displayName}
              >
                {user.attributes.profile.displayName}
              </NamedLink>
            ) : (
              <span className={css.displayName}>{user.attributes.profile.displayName}</span>
            )}
            <Badges className={css.badges} user={user} />
            <span className={css.postedAt}>{postedAt}</span>
          </div>

          {/* Comment content or edit form */}
          {isEditComment ? (
            <CommunityCommentForm
              id={`edit_comment_${commentId}`}
              currentUser={currentUser}
              addCommentInProgress={editCommentInProgress}
              addCommentError={editCommentError}
              submitMessageTranslationId="PostPage.editComment"
              onSubmit={handleEditSubmit}
              onCancel={handleEditCancel}
              rootClassName={css.editCommentForm}
              initialValues={{
                content: content,
              }}
            />
          ) : (
            commentHtml
          )}

          {/* Action buttons - hide when editing */}
          {!isEditComment && (
            <div className={css.actions}>
              {/* Voting UI */}
              <ActionButton className={css.voteActionButtonWrapper} isButton={false}>
                <InlineTextButton
                  className={classNames(css.voteActionButton, {
                    [css.voteActionButtonActive]: userVote === 'like',
                  })}
                  onClick={() => handleVote('like')}
                  disabled={isVoting}
                >
                  <IconVote className={css.voteActionIcon} direction="up" isActive={userVote === 'like'} />
                </InlineTextButton>
                <span className={css.voteCount}>{voteCount}</span>
                <InlineTextButton
                  className={classNames(css.voteActionButton, css.dislikeVoteActionButton, {
                    [css.voteActionButtonActive]: userVote === 'dislike',
                  })}
                  onClick={() => handleVote('dislike')}
                  disabled={isVoting}
                >
                  <IconVote
                    className={css.voteActionIcon}
                    direction="down"
                    isActive={userVote === 'dislike'}
                  />
                </InlineTextButton>
              </ActionButton>

              {/* Only show reply button if user is logged in */}
              {currentUser?.id && (
                <ActionButton label="Reply" onClick={handleStartReplying} disabled={isReplying}>
                  <IconComment className={css.actionIcon} />
                </ActionButton>
              )}
            </div>
          )}

          {/* Control Menu for edit/delete options */}
          {!isEditComment && isOwnComment && (
            <ControlMenu
              className={css.controlMenu}
              isOwn={isOwnComment}
              isReply={isReply}
              canPinComment={canPinComment}
              deleteCommentInProgress={deleteCommentInProgress}
              deleteCommentError={deleteCommentError}
              onEditComment={handleEdit}
              onDeleteComment={handleDelete}
              onPinComment={handlePinComment}
            />
          )}
        </div>

        {/* Reply form - separate from main comment content */}
        {isReplying && (
          <div className={css.replyFormContainer}>
            <CommunityCommentForm
              addCommentInProgress={isSubmittingReply}
              addCommentError={addCommentError}
              onSubmit={handleReplySubmit}
              onCancel={handleCancelReplying}
            />
          </div>
        )}

        {/* Replies container - separate from main comment content */}
        <div className={css.repliesWrapper}>
          {renderReplies()}
        </div>
      </div>
    </div>
  );
};

Comment.defaultProps = {
  currentUser: null,
  comment: null,
  allComments: [],
  addCommentError: null,
  deleteCommentError: null,
  editCommentInProgress: false,
  editCommentError: null,
  deletedCommentIds: [],
  currentDeleteCommentId: null,
  canPinComment: false,
  onEditComment: null,
  onPinComment: null,
  isReply: false,
  depth: 0,
};

Comment.propTypes = {
  // Context props
  state: object.isRequired,
  currentUser: propTypes.currentUser,

  // Comment data
  comment: shape({
    id: string.isRequired,
    userId: string.isRequired,
    content: string, // Optional since soft-deleted comments may have null content
    parentId: string, // For reply structure
    postedAt: string.isRequired,
    isDeleted: bool, // For soft-deleted comments
    deletedAt: string, // Deletion timestamp
  }),
  allComments: arrayOf(
    shape({
      id: string.isRequired,
      userId: string.isRequired,
      content: string, // Optional for soft-deleted comments
      parentId: string,
      postedAt: string.isRequired,
      isDeleted: bool,
      deletedAt: string,
    })
  ),

  // Action handlers
  onAddComment: func.isRequired,
  onDeleteComment: func.isRequired,
  onEditComment: func,
  onPinComment: func,

  // UI state props
  addCommentError: propTypes.error,
  deleteCommentInProgress: bool.isRequired,
  editCommentInProgress: bool,
  editCommentError: propTypes.error,
  deletedCommentIds: array.isRequired,
  currentDeleteCommentId: string,
  deleteCommentError: propTypes.error,
  canPinComment: bool,

  // Nesting props
  isReply: bool,
  depth: number,
};

export default React.memo(Comment);
